import React, { useState, useEffect } from 'react';
import { workspaceAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiGlobe, FiPlus, FiX, FiEdit2, FiTrash2, FiUsers, FiMapPin, FiCheck, FiSlash, FiMail, FiLock, FiUser, FiCopy } from 'react-icons/fi';

const typeColors = { international: '#8b5cf6', national: '#3b82f6', state: '#10b981', local: '#f59e0b' };
const typeLabels = { international: 'International', national: 'National', state: 'State', local: 'Local' };

const WorkspaceManagement = () => {
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [stats, setStats] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editWs, setEditWs] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [createdInfo, setCreatedInfo] = useState(null);
  const [planFeatures, setPlanFeatures] = useState({});
  const [allFeatures, setAllFeatures] = useState([]);

  const defaultFeatures = ['dashboard', 'surveillance', 'assessments', 'programs', 'vaccinations', 'vitals', 'sdoh', 'health-equity', 'reports', 'ai-insights', 'population', 'settings', 'notifications'];
  const initForm = {
    name: '', description: '', type: 'state', country: '', state: '', city: '', county: '',
    regions: '', departments: '',
    contact: { email: '', phone: '', address: '', website: '' },
    adminFirstName: '', adminLastName: '', adminEmail: '', adminPassword: '',
    plan: 'professional', maxUsers: 100, enabledFeatures: [...defaultFeatures]
  };
  const [form, setForm] = useState(initForm);

  const fetchData = async () => {
    try {
      const [wsRes, stRes, pfRes] = await Promise.all([
        workspaceAPI.getAll({ search, type: filterType }),
        workspaceAPI.getStats(),
        workspaceAPI.getPlanFeatures().catch(() => ({ data: {} }))
      ]);
      setWorkspaces(wsRes.data.workspaces || []);
      setStats(stRes.data);
      if (pfRes.data.planFeatures) setPlanFeatures(pfRes.data.planFeatures);
      if (pfRes.data.allFeatures) setAllFeatures(pfRes.data.allFeatures);
    } catch { toast.error('Failed to fetch workspaces'); }
  };

  useEffect(() => { fetchData(); }, [search, filterType]);

  const openCreate = () => { setEditWs(null); setForm(initForm); setCreatedInfo(null); setShowModal(true); };
  const openEdit = (ws) => {
    setEditWs(ws);
    setCreatedInfo(null);
    setForm({
      name: ws.name, description: ws.description || '', type: ws.type, country: ws.country || '',
      state: ws.state || '', city: ws.city || '', county: ws.county || '',
      regions: (ws.regions || []).join(', '), departments: (ws.departments || []).join(', '),
      contact: ws.contact || { email: '', phone: '', address: '', website: '' },
      adminFirstName: '', adminLastName: '', adminEmail: '', adminPassword: '',
      plan: ws.subscription?.plan || 'professional',
      maxUsers: ws.subscription?.maxUsers || 100,
      enabledFeatures: ws.subscription?.enabledFeatures || [...defaultFeatures]
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        regions: form.regions.split(',').map(r => r.trim()).filter(Boolean),
        departments: form.departments.split(',').map(d => d.trim()).filter(Boolean),
        subscription: { plan: form.plan, maxUsers: parseInt(form.maxUsers) || 100, enabledFeatures: form.enabledFeatures }
      };
      if (editWs) {
        await workspaceAPI.update(editWs._id, payload);
        toast.success('Workspace updated');
        setShowModal(false);
      } else {
        const { data } = await workspaceAPI.create(payload);
        toast.success(`Workspace created! Admin: ${data.admin?.email}`);
        setCreatedInfo(data);
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (ws) => {
    if (!window.confirm(`Delete "${ws.name}"? This cannot be undone.`)) return;
    try { await workspaceAPI.delete(ws._id); toast.success('Deleted'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  const handleToggle = async (ws) => {
    try { await workspaceAPI.update(ws._id, { isActive: !ws.isActive }); toast.success(ws.isActive ? 'Deactivated' : 'Activated'); fetchData(); }
    catch { toast.error('Toggle failed'); }
  };

  const switchToWorkspace = async (ws) => {
    try { await workspaceAPI.switchWorkspace({ workspaceId: ws._id }); localStorage.setItem('activeWorkspaceId', ws._id); toast.success(`Switched to ${ws.name}`); window.location.reload(); }
    catch { toast.error('Switch failed'); }
  };

  const copyText = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  const s = {
    card: { background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' },
    statCard: (color) => ({ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', borderLeft: `4px solid ${color}` }),
    btn: (bg = '#3b82f6', clr = '#fff') => ({ padding: '10px 20px', background: bg, color: clr, border: bg === '#f1f5f9' ? '1px solid #cbd5e1' : 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter,sans-serif', display: 'inline-flex', alignItems: 'center', gap: 6 }),
    input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
    label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 },
    badge: (color) => ({ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${color}15`, color })
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>Workspace Management</h1>
          <p style={{ color: '#64748b', marginTop: 4 }}>Create and manage independent workspaces for different regions, states, and countries</p>
        </div>
        <button onClick={openCreate} style={s.btn()}>
          <FiPlus size={18} /> Create Workspace
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[['Total Workspaces', stats.total || 0, '#3b82f6'], ['Active', stats.active || 0, '#10b981'], ['Inactive', stats.inactive || 0, '#ef4444'], ['Countries', (stats.byCountry || []).length, '#8b5cf6']].map(([label, val, color]) => (
          <div key={label} style={s.statCard(color)}>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', marginTop: 4 }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <input placeholder="Search workspaces..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...s.input, paddingLeft: 36 }} />
          <FiMapPin size={16} color="#94a3b8" style={{ position: 'absolute', left: 12, top: 12 }} />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...s.input, width: 'auto', minWidth: 150 }}>
          <option value="">All Types</option>
          <option value="international">International</option>
          <option value="national">National</option>
          <option value="state">State</option>
          <option value="local">Local</option>
        </select>
      </div>

      {/* Workspace Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
        {workspaces.map(ws => (
          <div key={ws._id} style={{ ...s.card, opacity: ws.isActive ? 1 : 0.6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: `${typeColors[ws.type]}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FiGlobe size={20} color={typeColors[ws.type]} />
                </div>
                <div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a', margin: 0 }}>{ws.name}</h3>
                  <span style={s.badge(typeColors[ws.type])}>{typeLabels[ws.type]}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => switchToWorkspace(ws)} title="Switch to workspace" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10b981', padding: 6 }}><FiCheck size={16} /></button>
                <button onClick={() => openEdit(ws)} title="Edit" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 6 }}><FiEdit2 size={16} /></button>
                <button onClick={() => handleToggle(ws)} title={ws.isActive ? 'Deactivate' : 'Activate'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: ws.isActive ? '#f59e0b' : '#10b981', padding: 6 }}><FiSlash size={16} /></button>
                <button onClick={() => handleDelete(ws)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 6 }}><FiTrash2 size={16} /></button>
              </div>
            </div>

            {ws.description && <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12, lineHeight: 1.5 }}>{ws.description}</p>}

            {/* Plan & User Limit */}
            {(() => {
              const plan = ws.subscription?.plan || 'professional';
              const maxU = ws.subscription?.maxUsers || 100;
              const approved = ws.approvedUserCount || 0;
              const pct = maxU > 0 ? Math.min((approved / maxU) * 100, 100) : 0;
              const planColors = { free: '#64748b', basic: '#3b82f6', standard: '#0ea5e9', professional: '#10b981', enterprise: '#8b5cf6' };
              const barColor = pct >= 90 ? '#ef4444' : pct >= 75 ? '#f59e0b' : '#10b981';
              const features = ws.subscription?.enabledFeatures || [];
              return (
                <div style={{ padding: 12, background: '#f8fafc', borderRadius: 10, marginBottom: 12, border: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${planColors[plan] || '#3b82f6'}15`, color: planColors[plan] || '#3b82f6', textTransform: 'capitalize' }}>
                        {plan}
                      </span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>{features.length} features</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: barColor }}>
                      {approved} / {maxU} users
                    </span>
                  </div>
                  <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 3, transition: 'width 0.3s' }} />
                  </div>
                  {pct >= 90 && (
                    <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4, fontWeight: 600 }}>
                      ⚠️ {pct >= 100 ? 'User limit reached — new registrations blocked' : 'Approaching user limit'}
                    </div>
                  )}
                  {features.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                      {features.filter(f => !['dashboard','settings','notifications'].includes(f)).map(f => (
                        <span key={f} style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: f === 'ai-insights' ? '#fef3c7' : '#f1f5f9', color: f === 'ai-insights' ? '#92400e' : '#64748b' }}>
                          {f === 'ai-insights' ? '🤖 AI' : f === 'health-equity' ? 'Health Eq.' : f.charAt(0).toUpperCase() + f.slice(1).replace(/-/g,' ')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Owner info */}
            {ws.owner && (
              <div style={{ padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <FiUser size={14} color="#16a34a" />
                <span style={{ color: '#15803d', fontWeight: 600 }}>Admin:</span>
                <span style={{ color: '#166534' }}>{ws.owner.firstName} {ws.owner.lastName}</span>
                <span style={{ color: '#4ade80' }}>({ws.owner.email})</span>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
              {ws.country && <div style={{ padding: 8, background: '#f8fafc', borderRadius: 6 }}><span style={{ color: '#94a3b8', fontWeight: 600 }}>Country:</span> <span style={{ color: '#334155' }}>{ws.country}</span></div>}
              {ws.state && <div style={{ padding: 8, background: '#f8fafc', borderRadius: 6 }}><span style={{ color: '#94a3b8', fontWeight: 600 }}>State:</span> <span style={{ color: '#334155' }}>{ws.state}</span></div>}
              <div style={{ padding: 8, background: '#f8fafc', borderRadius: 6 }}><span style={{ color: '#94a3b8', fontWeight: 600 }}>Users:</span> <span style={{ color: '#334155' }}>{ws.userCount || 0}</span></div>
              <div style={{ padding: 8, background: '#f8fafc', borderRadius: 6 }}><span style={{ color: '#94a3b8', fontWeight: 600 }}>Regions:</span> <span style={{ color: '#334155' }}>{(ws.regions || []).length}</span></div>
            </div>

            {ws.regions?.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {ws.regions.map(r => <span key={r} style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, background: '#f1f5f9', color: '#475569', fontWeight: 500 }}>{r}</span>)}
              </div>
            )}

            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8' }}>
              <span>Created {new Date(ws.createdAt).toLocaleDateString()}</span>
              <span style={{ color: ws.isActive ? '#10b981' : '#ef4444', fontWeight: 600 }}>● {ws.isActive ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
        ))}
      </div>

      {workspaces.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <FiGlobe size={48} style={{ marginBottom: 16 }} />
          <p style={{ fontSize: 16, fontWeight: 600 }}>No workspaces found</p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, padding: 32, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontSize: 22, fontWeight: 700 }}>{editWs ? 'Edit Workspace' : 'Create New Workspace'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={22} /></button>
            </div>

            {/* Success state after creation */}
            {createdInfo && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ padding: 20, background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <FiCheck size={20} color="#16a34a" />
                    <span style={{ fontSize: 16, fontWeight: 700, color: '#15803d' }}>Workspace Created Successfully!</span>
                  </div>
                  <p style={{ fontSize: 14, color: '#166534', marginBottom: 16 }}>
                    <strong>{createdInfo.workspace?.name}</strong> is ready. Share these credentials with the workspace admin:
                  </p>
                  <div style={{ background: '#fff', borderRadius: 8, padding: 16, border: '1px solid #dcfce7' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>LOGIN EMAIL</div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{createdInfo.admin?.email}</div>
                      </div>
                      <button onClick={() => copyText(createdInfo.admin?.email)} style={{ ...s.btn('#f1f5f9', '#475569'), padding: '6px 12px', fontSize: 12 }}><FiCopy size={14} /> Copy</button>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>PASSWORD</div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{form.adminPassword}</div>
                      </div>
                      <button onClick={() => copyText(form.adminPassword)} style={{ ...s.btn('#f1f5f9', '#475569'), padding: '6px 12px', fontSize: 12 }}><FiCopy size={14} /> Copy</button>
                    </div>
                  </div>
                  <p style={{ fontSize: 12, color: '#64748b', marginTop: 12, fontStyle: 'italic' }}>
                    ⚠️ Save these credentials — the password cannot be retrieved later.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
                  <button onClick={() => { setShowModal(false); setCreatedInfo(null); }} style={s.btn()}>Done</button>
                  <button onClick={() => { setCreatedInfo(null); setForm(initForm); }} style={s.btn('#f1f5f9', '#475569')}>Create Another</button>
                </div>
              </div>
            )}

            {/* Form */}
            {!createdInfo && (
              <form onSubmit={handleSubmit}>
                <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {/* Workspace Details Section */}
                  <div style={{ gridColumn: '1 / -1', padding: '12px 16px', background: '#f0f9ff', borderRadius: 10, border: '1px solid #bfdbfe' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FiGlobe size={16} /> Workspace Details
                    </div>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={s.label}>Workspace Name *</label>
                    <input style={s.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Virginia, Gujarat, Florida" required />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={s.label}>Description</label>
                    <textarea style={{ ...s.input, minHeight: 60, resize: 'vertical' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description..." />
                  </div>
                  <div>
                    <label style={s.label}>Level / Type *</label>
                    <select style={s.input} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                      <option value="international">International</option>
                      <option value="national">National</option>
                      <option value="state">State</option>
                      <option value="local">Local</option>
                    </select>
                  </div>
                  <div><label style={s.label}>Country</label><input style={s.input} value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} placeholder="e.g., United States, India" /></div>
                  <div><label style={s.label}>State / Province</label><input style={s.input} value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} placeholder="e.g., Virginia, Gujarat" /></div>
                  <div><label style={s.label}>City</label><input style={s.input} value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="e.g., Richmond" /></div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={s.label}>Regions (comma-separated)</label>
                    <input style={s.input} value={form.regions} onChange={e => setForm({ ...form, regions: e.target.value })} placeholder="e.g., Northern Virginia, Hampton Roads" />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={s.label}>Departments (comma-separated)</label>
                    <input style={s.input} value={form.departments} onChange={e => setForm({ ...form, departments: e.target.value })} placeholder="e.g., Epidemiology, Community Health" />
                  </div>

                  {/* Admin Account Section — only for creation */}
                  {!editWs && (
                    <>
                      <div style={{ gridColumn: '1 / -1', padding: '12px 16px', background: '#fef3c7', borderRadius: 10, border: '1px solid #fde68a', marginTop: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FiUser size={16} /> Workspace Admin Account
                        </div>
                        <div style={{ fontSize: 12, color: '#a16207', marginTop: 4 }}>This admin will manage the workspace — they can create users, manage data, and access all features within their workspace.</div>
                      </div>
                      <div><label style={s.label}>Admin First Name *</label><input style={s.input} value={form.adminFirstName} onChange={e => setForm({ ...form, adminFirstName: e.target.value })} placeholder="e.g., John" required /></div>
                      <div><label style={s.label}>Admin Last Name *</label><input style={s.input} value={form.adminLastName} onChange={e => setForm({ ...form, adminLastName: e.target.value })} placeholder="e.g., Smith" required /></div>
                      <div>
                        <label style={s.label}><FiMail size={12} style={{ marginRight: 4 }} />Admin Email *</label>
                        <input style={s.input} type="email" value={form.adminEmail} onChange={e => setForm({ ...form, adminEmail: e.target.value })} placeholder="admin@virginia-health.gov" required />
                      </div>
                      <div>
                        <label style={s.label}><FiLock size={12} style={{ marginRight: 4 }} />Admin Password *</label>
                        <input style={s.input} type="text" value={form.adminPassword} onChange={e => setForm({ ...form, adminPassword: e.target.value })} placeholder="Min 6 characters" required minLength={6} />
                      </div>
                    </>
                  )}

                  {/* Subscription & User Limits */}
                  <div style={{ gridColumn: '1 / -1', padding: '12px 16px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #bbf7d0', marginTop: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FiUsers size={16} /> Subscription & User Limits
                    </div>
                    <div style={{ fontSize: 12, color: '#15803d', marginTop: 4 }}>Control how many users can register and be approved in this workspace.</div>
                  </div>
                  <div>
                    <label style={s.label}>Plan</label>
                    <select style={s.input} value={form.plan} onChange={e => {
                      const plan = e.target.value;
                      const presets = { free: 3, basic: 25, standard: 25, professional: 100, enterprise: 500 };
                      const features = planFeatures[plan] || defaultFeatures;
                      setForm({ ...form, plan, maxUsers: presets[plan] || form.maxUsers, enabledFeatures: [...features] });
                    }}>
                      <option value="free">Community (Free)</option>
                      <option value="basic">Basic</option>
                      <option value="standard">Standard</option>
                      <option value="professional">Professional</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div>
                    <label style={s.label}>Max Users</label>
                    <input style={s.input} type="number" min="1" max="10000" value={form.maxUsers} onChange={e => setForm({ ...form, maxUsers: e.target.value })} placeholder="100" />
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                      Free=3, Basic=25, Standard=25, Pro=100, Enterprise=500
                    </div>
                  </div>

                  {/* Feature Access Control */}
                  <div style={{ gridColumn: '1 / -1', padding: '12px 16px', background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe', marginTop: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FiCheck size={16} /> Feature Access Control
                    </div>
                    <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 4 }}>
                      Toggle which modules this workspace can access. Plan presets auto-apply when you change the plan above.
                    </div>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    {(() => {
                      const cats = { core: 'Core Modules', modules: 'Extended Modules', analytics: 'Analytics & AI', system: 'System' };
                      const features = allFeatures.length > 0 ? allFeatures : [
                        { key: 'dashboard', label: 'Dashboard', category: 'core', alwaysOn: true },
                        { key: 'surveillance', label: 'Disease Surveillance', category: 'core' },
                        { key: 'vaccinations', label: 'Vaccinations', category: 'core' },
                        { key: 'vitals', label: 'Vital Statistics', category: 'core' },
                        { key: 'assessments', label: 'Assessments', category: 'modules' },
                        { key: 'programs', label: 'Health Programs', category: 'modules' },
                        { key: 'sdoh', label: 'SDOH Tracker', category: 'modules' },
                        { key: 'health-equity', label: 'Health Equity', category: 'analytics' },
                        { key: 'reports', label: 'Reports & Analytics', category: 'analytics' },
                        { key: 'ai-insights', label: 'AI Insights', category: 'analytics' },
                        { key: 'population', label: 'Population Data', category: 'analytics' },
                        { key: 'settings', label: 'Settings', category: 'system', alwaysOn: true },
                        { key: 'notifications', label: 'Notifications', category: 'system', alwaysOn: true },
                      ];
                      const grouped = {};
                      features.forEach(f => {
                        if (!grouped[f.category]) grouped[f.category] = [];
                        grouped[f.category].push(f);
                      });
                      const toggleFeature = (key) => {
                        const ef = form.enabledFeatures || [];
                        if (ef.includes(key)) {
                          setForm({ ...form, enabledFeatures: ef.filter(k => k !== key) });
                        } else {
                          setForm({ ...form, enabledFeatures: [...ef, key] });
                        }
                      };
                      return Object.entries(grouped).map(([catKey, items]) => (
                        <div key={catKey} style={{ marginBottom: 16 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{cats[catKey] || catKey}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                            {items.map(f => {
                              const enabled = (form.enabledFeatures || []).includes(f.key);
                              const locked = f.alwaysOn;
                              return (
                                <div key={f.key}
                                  onClick={() => !locked && toggleFeature(f.key)}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                                    borderRadius: 10, cursor: locked ? 'default' : 'pointer',
                                    background: enabled ? '#f0fdf4' : '#fafafa',
                                    border: `1.5px solid ${enabled ? '#86efac' : '#e5e7eb'}`,
                                    transition: 'all 0.2s', opacity: locked ? 0.7 : 1,
                                  }}>
                                  <div style={{
                                    width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: enabled ? '#10b981' : '#e5e7eb',
                                    color: '#fff', fontSize: 12, fontWeight: 800,
                                    transition: 'background 0.2s',
                                  }}>
                                    {enabled ? '✓' : ''}
                                  </div>
                                  <div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: enabled ? '#166534' : '#6b7280' }}>{f.label}</div>
                                    {locked && <div style={{ fontSize: 10, color: '#94a3b8' }}>Always on</div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ));
                    })()}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => setForm({ ...form, enabledFeatures: [...defaultFeatures] })} style={{ ...s.btn('#e0f2fe', '#0369a1'), padding: '6px 14px', fontSize: 12 }}>All Features</button>
                      <button type="button" onClick={() => {
                        const pf = planFeatures[form.plan] || defaultFeatures;
                        setForm({ ...form, enabledFeatures: [...pf] });
                      }} style={{ ...s.btn('#fef3c7', '#92400e'), padding: '6px 14px', fontSize: 12 }}>Reset to Plan Default</button>
                      <span style={{ fontSize: 12, color: '#64748b', lineHeight: '30px', marginLeft: 8 }}>
                        {(form.enabledFeatures || []).length} features enabled
                      </span>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div style={{ gridColumn: '1 / -1', padding: '12px 16px', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', marginTop: 8 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#475569' }}>Contact Information (Optional)</div>
                  </div>
                  <div><label style={s.label}>Contact Email</label><input style={s.input} type="email" value={form.contact.email} onChange={e => setForm({ ...form, contact: { ...form.contact, email: e.target.value } })} /></div>
                  <div><label style={s.label}>Phone</label><input style={s.input} value={form.contact.phone} onChange={e => setForm({ ...form, contact: { ...form.contact, phone: e.target.value } })} /></div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={s.label}>Address</label><input style={s.input} value={form.contact.address} onChange={e => setForm({ ...form, contact: { ...form.contact, address: e.target.value } })} />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
                  <button type="button" onClick={() => setShowModal(false)} style={s.btn('#f1f5f9', '#475569')}>Cancel</button>
                  <button type="submit" style={s.btn()}>{editWs ? 'Update Workspace' : 'Create Workspace & Admin'}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceManagement;
